import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api, { getCurrentUserId, getMyStudentId } from "../../services/api";
import "./Agents.css";
import "./Analytics.css";

const analyticsAPI = {
  async fetchAnalytics(studentId, timePeriod = "all") {
    const response = await api.post("/api/agents/analytics/generate", {
      student_id: studentId,
      time_period: timePeriod,
      analysis_type: "comprehensive",
    });

    if (response.data && response.data.success) {
      // Extract the actual analytics data from the nested 'data' field
      return response.data.data || response.data;
    } else {
      throw new Error(response.data?.error || "Failed to fetch analytics");
    }
  },
};

/**
 * ============================================================================
 * DISPLAY FORMATTERS
 * ============================================================================
 */

// Friendly labels for enum-like codes returned by the analytics API
const LEVEL_LABELS = {
  excellent: "Excellent",
  good: "Good",
  passing: "Passing",
  pass: "Passing",
  failing: "Needs improvement",
  needs_improvement: "Needs improvement",
  needs_work: "Needs improvement",
  mastered: "Mastered",
  proficient: "Proficient",
  developing: "Developing",
  beginner: "Beginner",
  improving: "Improving",
  declining: "Declining",
  stable: "Stable",
  above_average: "Above average",
  below_average: "Below average",
  average: "Average",
  insufficient_data: "Not enough data",
};

// "needs_improvement" / "Needs_improvement" -> "Needs improvement"
const formatLevel = (code) => {
  if (code === null || code === undefined || code === "") return "";
  const raw = String(code).trim();
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (LEVEL_LABELS[key]) return LEVEL_LABELS[key];
  const words = raw.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
};

// "fractions" -> "Fractions", "basic_science" -> "Basic Science"
const SMALL_WORDS = new Set(["and", "of", "the", "in", "on", "a", "an", "to", "for"]);
const toTitleCase = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).replace(/_/g, " ").replace(/\s+/g, " ").trim();
  return text
    .split(" ")
    .map((word, i) => {
      if (!word) return word;
      // Keep acronyms / class codes such as "JSS1" or "BODMAS" as-is
      if (word.length > 1 && word === word.toUpperCase() && /[A-Z]/.test(word)) {
        return word;
      }
      const lower = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

// Title-case topic names quoted inside recommendation sentences, e.g.
// "Focus on improving 'fractions'" -> "Focus on improving Fractions"
const formatRecommendation = (rec) => {
  let text = rec;
  if (rec && typeof rec === "object") {
    text = rec.text || rec.message || rec.recommendation || rec.description || "";
  }
  if (typeof text !== "string") return "";
  return text.replace(
    /(['"\u2018\u201C])([^'"\u2018\u2019\u201C\u201D]{1,60})(['"\u2019\u201D])/g,
    (_m, _open, topic) => toTitleCase(topic)
  );
};

const isNum = (v) =>
  v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

// Safe number / percentage renderers: never show "undefined%" or "NaN%"
const fmtNum = (v, fallback = "\u2014") =>
  isNum(v) ? Math.round(Number(v) * 10) / 10 : fallback;
const fmtPct = (v, fallback = "\u2014") =>
  isNum(v) ? `${Math.round(Number(v) * 10) / 10}%` : fallback;

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

/**
 * ============================================================================
 * UI COMPONENTS
 * ============================================================================
 */

// Loading Component
const LoadingSpinner = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>Analyzing your learning data...</p>
  </div>
);

// Error Component
const ErrorMessage = ({ error, onRetry }) => (
  <div className="error-container">
    <div className="error-icon">⚠️</div>
    <h3>Unable to Load Analytics</h3>
    <p>{error}</p>
    <button className="btn btn-primary" onClick={onRetry}>
      Try Again
    </button>
  </div>
);

// Empty State Component
const EmptyState = () => (
  <div className="empty-state-container">
    <div className="empty-icon">📊</div>
    <h2>No Analytics Data Yet</h2>
    <p>Take some quizzes to see your comprehensive analytics and insights!</p>
  </div>
);

// Timeframe Selector
const TimeframeSelector = ({ value, onChange }) => {
  const options = [
    { value: "week", label: "This Week", icon: "📅" },
    { value: "month", label: "This Month", icon: "📆" },
    { value: "quarter", label: "This Quarter", icon: "📊" },
    { value: "all", label: "All Time", icon: "⏳" },
  ];

  return (
    <div className="timeframe-selector">
      {options.map((option) => (
        <button
          key={option.value}
          className={`timeframe-btn ${value === option.value ? "active" : ""}`}
          onClick={() => onChange(option.value)}
        >
          <span className="timeframe-icon">{option.icon}</span>
          <span className="timeframe-label">{option.label}</span>
        </button>
      ))}
    </div>
  );
};

// Overall Stats Cards
const OverallStatsCards = ({ stats }) => {
  const cards = [
    {
      label: "Total Attempts",
      value: fmtNum(stats.total_attempts, 0),
      icon: "📝",
      color: "#0B1B3F",
    },
    {
      label: "Average Score",
      value: fmtPct(stats.average_score),
      icon: "🎯",
      color: "#D9950F",
    },
    {
      label: "Pass Rate",
      value: fmtPct(stats.pass_rate),
      icon: "✅",
      color: "#2A4F9E",
    },
    {
      label: "Topics Covered",
      value: fmtNum(stats.coverage?.unique_topics, 0),
      icon: "📚",
      color: "#0B1B3F",
    },
  ];

  return (
    <div className="stats-cards-grid">
      {cards.map((card, idx) => (
        <div
          key={idx}
          className="stat-card"
          style={{ borderLeftColor: card.color }}
        >
          <div className="stat-icon" style={{ color: card.color }}>
            {card.icon}
          </div>
          <div className="stat-details">
            <p className="stat-label">{card.label}</p>
            <p className="stat-value">{card.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Performance Overview
const PerformanceOverview = ({ stats }) => {
  const rawDist = stats.performance_distribution || {};
  const dist = {
    excellent: Number(rawDist.excellent) || 0,
    good: Number(rawDist.good) || 0,
    passing: Number(rawDist.passing) || 0,
    failing: Number(rawDist.failing) || 0,
  };
  const total = dist.excellent + dist.good + dist.passing + dist.failing;

  const segments = [
    { label: "Excellent", count: dist.excellent, color: "#10b981", icon: "🌟" },
    { label: "Good", count: dist.good, color: "#3b82f6", icon: "👍" },
    { label: "Passing", count: dist.passing, color: "#f59e0b", icon: "✓" },
    { label: "Needs Work", count: dist.failing, color: "#ef4444", icon: "📌" },
  ];

  return (
    <div className="performance-overview-card">
      <h3>Performance Distribution</h3>
      <div className="performance-segments">
        {segments.map((segment) => {
          const percentage = total > 0 ? (segment.count / total) * 100 : 0;
          return (
            <div key={segment.label} className="segment-item">
              <div className="segment-header">
                <span className="segment-icon">{segment.icon}</span>
                <span className="segment-label">{segment.label}</span>
                <span className="segment-count">{segment.count}</span>
              </div>
              <div className="segment-bar">
                <div
                  className="segment-fill"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: segment.color,
                  }}
                />
              </div>
              <span className="segment-percentage">
                {percentage.toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>

      <div className="stats-summary">
        <div className="summary-item">
          <span className="summary-label">Highest Score:</span>
          <span className="summary-value">{fmtPct(stats.highest_score)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Average Score:</span>
          <span className="summary-value">{fmtPct(stats.average_score)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Accuracy:</span>
          <span className="summary-value">
            {fmtPct(stats.questions?.accuracy)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Subject Performance Grid
const SubjectPerformanceGrid = ({ subjects }) => {
  const getScoreColor = (score) => {
    if (score >= 85) return "#10b981";
    if (score >= 70) return "#3b82f6";
    if (score >= 50) return "#f59e0b";
    return "#ef4444";
  };

  const getTrendIcon = (trend) => {
    if (trend === "improving") return "📈";
    if (trend === "declining") return "📉";
    return "➡️";
  };

  if (!subjects || subjects.length === 0) {
    return (
      <div className="empty-section">
        <p>No subject data available yet.</p>
      </div>
    );
  }

  return (
    <div className="subjects-grid">
      {subjects.map((subject, idx) => (
        <div key={idx} className="subject-card">
          <div className="subject-header">
            <h4>{toTitleCase(subject.subject) || "Subject"}</h4>
            <span className="trend-badge">{getTrendIcon(subject.trend)}</span>
          </div>

          <div className="subject-score">
            <div
              className="score-circle"
              style={{ borderColor: getScoreColor(subject.average_score) }}
            >
              <span
                className="score-number"
                style={{ color: getScoreColor(subject.average_score) }}
              >
                {fmtPct(subject.average_score)}
              </span>
            </div>
          </div>

          <div className="subject-stats">
            <div className="subject-stat">
              <span className="stat-label">Attempts:</span>
              <span className="stat-value">{fmtNum(subject.attempts, 0)}</span>
            </div>
            <div className="subject-stat">
              <span className="stat-label">Topics:</span>
              <span className="stat-value">
                {fmtNum(subject.topics_covered, 0)}
              </span>
            </div>
          </div>

          {subject.performance_level && (
            <div
              className="performance-badge"
              style={{ backgroundColor: getScoreColor(subject.average_score) }}
            >
              {formatLevel(subject.performance_level)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Topic Performance Table
const TopicPerformanceTable = ({ topics }) => {
  const getScoreColor = (score) => {
    if (score >= 85) return "#10b981";
    if (score >= 70) return "#3b82f6";
    if (score >= 50) return "#f59e0b";
    return "#ef4444";
  };

  if (!topics || topics.length === 0) {
    return (
      <div className="empty-section">
        <p>No topic data available yet.</p>
      </div>
    );
  }

  return (
    <div className="topic-table-container">
      <table className="topic-table">
        <thead>
          <tr>
            <th>Topic</th>
            <th>Avg Score</th>
            <th>Attempts</th>
            <th>Pass Rate</th>
            <th>Accuracy</th>
            <th>Level</th>
          </tr>
        </thead>
        <tbody>
          {topics.map((topic, idx) => (
            <tr key={idx}>
              <td className="topic-name">{toTitleCase(topic.topic)}</td>
              <td>
                <span
                  className="score-badge"
                  style={{
                    backgroundColor: getScoreColor(topic.average_score),
                  }}
                >
                  {fmtPct(topic.average_score)}
                </span>
              </td>
              <td>{fmtNum(topic.attempts, 0)}</td>
              <td>{fmtPct(topic.pass_rate)}</td>
              <td>{fmtPct(topic.accuracy)}</td>
              <td>
                {topic.performance_level ? (
                  <span className="level-badge">
                    {formatLevel(topic.performance_level)}
                  </span>
                ) : (
                  "\u2014"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Trends Chart
const TrendsChart = ({ trends: rawTrends }) => {
  const trends = rawTrends || {};
  const hasComparison =
    isNum(trends.recent_average) && isNum(trends.older_average);
  const hasChange =
    isNum(trends.change_percentage) && Number(trends.change_percentage) !== 0;
  const getTrendColor = (direction) => {
    if (direction === "up") return "#10b981";
    if (direction === "down") return "#ef4444";
    return "#64748b";
  };

  const getTrendMessage = (trend) => {
    if (trend === "improving") return "Your performance is improving! 🎉";
    if (trend === "declining") return "Let's focus on improvement 💪";
    if (trend === "stable") return "Maintaining consistent performance 👍";
    return "Keep practicing to see trends";
  };

  return (
    <div className="trends-card">
      <h3>Performance Trends</h3>

      <div className="trend-overview">
        <div
          className="trend-indicator"
          style={{ borderColor: getTrendColor(trends.trend_direction) }}
        >
          <div
            className="trend-arrow"
            style={{ color: getTrendColor(trends.trend_direction) }}
          >
            {trends.trend_direction === "up"
              ? "↗"
              : trends.trend_direction === "down"
              ? "↘"
              : "→"}
          </div>
          <p
            className="trend-message"
            style={{ color: getTrendColor(trends.trend_direction) }}
          >
            {getTrendMessage(trends.overall_trend)}
          </p>
        </div>

        {hasComparison ? (
          hasChange && (
            <div className="trend-change">
              <p>
                {Number(trends.change_percentage) > 0 ? "+" : ""}
                {fmtPct(trends.change_percentage)} change
              </p>
              <div className="trend-comparison">
                <span>Recent: {fmtPct(trends.recent_average)}</span>
                <span>Previous: {fmtPct(trends.older_average)}</span>
              </div>
            </div>
          )
        ) : (
          <div className="trend-change">
            <p>Take another quiz to see your trend</p>
          </div>
        )}
      </div>

      {trends.weekly_trend && trends.weekly_trend.length > 0 && (
        <div className="weekly-trend">
          <h4>Weekly Progress</h4>
          <div className="weekly-bars">
            {trends.weekly_trend.map((week, idx) => (
              <div
                key={idx}
                className="week-bar"
                title={fmtPct(week.average_score)}
              >
                <div
                  className="week-fill"
                  style={{
                    height: `${Math.min(100, Math.max(0, Number(week.average_score) || 0))}%`,
                    backgroundColor:
                      week.average_score >= 70 ? "#10b981" : "#3b82f6",
                  }}
                />
                <span className="week-label">
                  {typeof week.week === "string" && week.week.includes("-W")
                    ? `W${week.week.split("-W")[1]}`
                    : week.week || idx + 1}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Insights Panel
const InsightsPanel = ({ insights: rawInsights, recommendations }) => {
  const insights = rawInsights || {};
  const recs = (Array.isArray(recommendations) ? recommendations : [])
    .map(formatRecommendation)
    .filter(Boolean);
  const hasAny =
    insights.strengths?.length ||
    insights.weaknesses?.length ||
    insights.improving_areas?.length ||
    recs.length;
  if (!hasAny) return null;
  return (
    <div className="insights-panel">
      {/* Strengths */}
      {insights.strengths && insights.strengths.length > 0 && (
        <div className="insight-card strength-card">
          <h3>⭐ Your Strengths</h3>
          <ul className="insight-list">
            {insights.strengths.map((item, idx) => (
              <li key={idx} className="insight-item">
                <span className="topic-name">{toTitleCase(item.topic)}</span>
                <span className="score-badge excellent">
                  {fmtPct(item.average_score)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Weaknesses */}
      {insights.weaknesses && insights.weaknesses.length > 0 && (
        <div className="insight-card weakness-card">
          <h3>📌 Focus Areas</h3>
          <ul className="insight-list">
            {insights.weaknesses.map((item, idx) => (
              <li key={idx} className="insight-item">
                <span className="topic-name">{toTitleCase(item.topic)}</span>
                <span className="score-badge needs-work">
                  {fmtPct(item.average_score)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Improving Areas */}
      {insights.improving_areas && insights.improving_areas.length > 0 && (
        <div className="insight-card improving-card">
          <h3>📈 Great Progress</h3>
          <ul className="insight-list">
            {insights.improving_areas.map((item, idx) => (
              <li key={idx} className="insight-item">
                <span className="topic-name">{toTitleCase(item.topic)}</span>
                {isNum(item.improvement) && (
                  <span className="improvement-badge">
                    +{fmtPct(item.improvement)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {recs.length > 0 && (
        <div className="insight-card recommendations-card">
          <h3>💡 Recommendations</h3>
          <ul className="recommendations-list">
            {recs.map((rec, idx) => (
              <li key={idx} className="recommendation-item">
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Study Patterns Card
const StudyPatternsCard = ({ patterns }) => {
  if (!patterns || Object.keys(patterns).length === 0) {
    return null;
  }

  const getTimeDisplay = (rawHour) => {
    if (!isNum(rawHour)) return "Not enough data";
    const hour = Number(rawHour);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const getConsistencyColor = (score) => {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#3b82f6";
    if (score >= 40) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div className="study-patterns-card">
      <h3>🎯 Study Patterns</h3>
      <div className="patterns-grid">
        <div className="pattern-item">
          <div className="pattern-icon">⏰</div>
          <div className="pattern-content">
            <p className="pattern-label">Most Active Time</p>
            <p className="pattern-value">
              {getTimeDisplay(patterns.most_active_hour)}
            </p>
          </div>
        </div>

        <div className="pattern-item">
          <div className="pattern-icon">📅</div>
          <div className="pattern-content">
            <p className="pattern-label">Favorite Day</p>
            <p className="pattern-value">
              {patterns.most_active_day || "Not enough data"}
            </p>
          </div>
        </div>

        <div className="pattern-item">
          <div className="pattern-icon">🔄</div>
          <div className="pattern-content">
            <p className="pattern-label">Consistency</p>
            <p
              className="pattern-value"
              style={{ color: getConsistencyColor(patterns.consistency_score) }}
            >
              {fmtPct(patterns.consistency_score, "Not enough data")}
            </p>
          </div>
        </div>

        <div className="pattern-item">
          <div className="pattern-icon">📊</div>
          <div className="pattern-content">
            <p className="pattern-label">Study Frequency</p>
            <p className="pattern-value">
              {patterns.study_frequency
                ? formatLevel(patterns.study_frequency)
                : "Not enough data"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Peer Comparison Card
const PeerComparisonCard = ({ comparison }) => {
  if (
    !comparison ||
    comparison.comparison === "insufficient_data" ||
    comparison.comparison === "insufficient_peer_data" ||
    !isNum(comparison.student_average) ||
    !isNum(comparison.peer_average)
  ) {
    return null;
  }

  const getComparisonMessage = (comp) => {
    if (comp === "above_average")
      return "You're performing above the class average! 🌟";
    if (comp === "below_average")
      return "Keep practicing to reach the class average 💪";
    return "You're performing at the class average 👍";
  };

  const getComparisonColor = (comp) => {
    if (comp === "above_average") return "#10b981";
    if (comp === "below_average") return "#ef4444";
    return "#3b82f6";
  };

  return (
    <div className="peer-comparison-card">
      <h3>📊 Peer Comparison</h3>
      <div className="comparison-content">
        <div className="comparison-bars">
          <div className="comparison-bar">
            <span className="bar-label">Your Average</span>
            <div className="bar-container">
              <div
                className="bar-fill"
                style={{
                  width: `${comparison.student_average}%`,
                  backgroundColor: getComparisonColor(comparison.comparison),
                }}
              />
            </div>
            <span className="bar-value">
              {fmtPct(comparison.student_average)}
            </span>
          </div>

          <div className="comparison-bar">
            <span className="bar-label">Class Average</span>
            <div className="bar-container">
              <div
                className="bar-fill"
                style={{
                  width: `${comparison.peer_average}%`,
                  backgroundColor: "#94a3b8",
                }}
              />
            </div>
            <span className="bar-value">{fmtPct(comparison.peer_average)}</span>
          </div>
        </div>

        <p
          className="comparison-message"
          style={{ color: getComparisonColor(comparison.comparison) }}
        >
          {getComparisonMessage(comparison.comparison)}
        </p>

        {isNum(comparison.difference) && Number(comparison.difference) !== 0 && (
          <p className="comparison-difference">
            {Number(comparison.difference) > 0 ? "+" : ""}
            {fmtPct(comparison.difference)} difference
          </p>
        )}
      </div>
    </div>
  );
};

// Recent Activities
const RecentActivities = ({ activities }) => {
  if (!activities || activities.length === 0) {
    return (
      <div className="empty-section">
        <p>No recent activities to display.</p>
      </div>
    );
  }

  return (
    <div className="activities-list">
      {activities.map((activity, idx) => (
        <div key={idx} className="activity-item">
          <div className="activity-icon">{activity.passed ? "✅" : "📝"}</div>
          <div className="activity-details">
            <p className="activity-topic">
              {[toTitleCase(activity.topic), toTitleCase(activity.subject)]
                .filter(Boolean)
                .join(" - ") || "Quiz"}
            </p>
            <p className="activity-score">
              {isNum(activity.score) && isNum(activity.total)
                ? `Score: ${activity.score}/${activity.total}`
                : "Score"}
              {isNum(activity.percentage) &&
                ` (${fmtPct(activity.percentage)})`}
            </p>
          </div>
          <div className="activity-date">{fmtDate(activity.timestamp)}</div>
        </div>
      ))}
    </div>
  );
};

/**
 * ============================================================================
 * MAIN COMPONENT
 * ============================================================================
 */
const AnalyticsAgent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeframe, setTimeframe] = useState("all");
  const navigate = useNavigate();

  // Get student ID
  const getStudentId = () => {
    return getMyStudentId();
  };

  // Load analytics data
  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const studentId = getStudentId();
      console.log(
        "Fetching analytics for student:",
        studentId,
        "timeframe:",
        timeframe
      );
      const data = await analyticsAPI.fetchAnalytics(studentId, timeframe);
      console.log("Analytics data received:", data);
      console.log("Overall stats:", data?.overall_stats);
      setAnalyticsData(data);
    } catch (err) {
      console.error("Analytics fetch error:", err);
      setError(err.message || "Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  // Load on mount and timeframe change
  useEffect(() => {
    loadAnalytics();
  }, [timeframe]);

  // Listen for quiz completion events
  useEffect(() => {
    const handleQuizCompleted = (event) => {
      console.log(
        "🎯 Quiz completed event received in Analytics:",
        event.detail
      );
      setTimeout(() => {
        loadAnalytics();
      }, 2000); // Small delay to ensure backend processing is complete
    };

    window.addEventListener("quizCompleted", handleQuizCompleted);
    return () =>
      window.removeEventListener("quizCompleted", handleQuizCompleted);
  }, []);

  // Render loading state
  if (loading) {
    return (
      <div className="analytics-container">
        <LoadingSpinner />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h1>Learning Analytics</h1>
        </div>
        <ErrorMessage error={error} onRetry={loadAnalytics} />
      </div>
    );
  }

  // Render empty state
  if (
    !analyticsData ||
    !analyticsData.overall_stats ||
    analyticsData.overall_stats.total_attempts === 0
  ) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h1>Learning Analytics</h1>
          <p className="header-subtitle">
            Comprehensive insights into your learning journey
          </p>
        </div>
        <EmptyState />
        <div className="empty-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/student-dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const {
    overall_stats,
    subject_performance,
    topic_performance,
    trends,
    insights,
    peer_comparison,
    study_patterns,
    recent_activities,
    recommendations,
  } = analyticsData;

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-content">
          <h1>📊 Learning Analytics</h1>
          <p className="header-subtitle">
            Comprehensive insights and performance metrics
          </p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/student-dashboard")}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Timeframe Selector */}
      <TimeframeSelector value={timeframe} onChange={setTimeframe} />

      {/* Main Content */}
      <div className="analytics-content">
        {/* Overall Stats */}
        <OverallStatsCards stats={overall_stats} />

        {/* Performance Overview */}
        <PerformanceOverview stats={overall_stats} />

        {/* Trends */}
        <TrendsChart trends={trends} />

        {/* Subject Performance */}
        <section className="analytics-section">
          <h2>📚 Subject Performance</h2>
          <SubjectPerformanceGrid subjects={subject_performance} />
        </section>

        {/* Topic Performance */}
        <section className="analytics-section">
          <h2>📖 Topic Breakdown</h2>
          <TopicPerformanceTable topics={topic_performance} />
        </section>

        {/* Insights & Recommendations */}
        <InsightsPanel insights={insights} recommendations={recommendations} />

        {/* Study Patterns */}
        <StudyPatternsCard patterns={study_patterns} />

        {/* Peer Comparison */}
        <PeerComparisonCard comparison={peer_comparison} />

        {/* Recent Activities */}
        <section className="analytics-section">
          <h2>📝 Recent Activities</h2>
          <RecentActivities activities={recent_activities} />
        </section>
      </div>

      {/* Footer */}
      <div className="analytics-footer">
        <button className="btn btn-secondary" onClick={loadAnalytics}>
          🔄 Refresh Analytics
        </button>
        <div className="footer-tip">
          <p>
            💡 <strong>Pro Tip:</strong> Use these analytics to identify your
            strengths and areas for improvement. Focus on topics with lower
            scores and practice consistently to see improvement over time!
          </p>
        </div>
      </div>
    </div>
  );
};

// Wrap in the shared layout so the page has the site header and footer
const AnalyticsAgentPage = (props) => (
  <Layout>
    <AnalyticsAgent {...props} />
  </Layout>
);

export default AnalyticsAgentPage;
